import { useEffect, useMemo, useRef, useState } from 'react';

function cleanText(value) {
  return String(value || '').trim();
}

function normalizeOption(option) {
  if (option && typeof option === 'object') {
    const label = cleanText(option.label || option.name || option.title || option.value);
    const value = cleanText(option.value || option.key || label);

    return label && value ? { label, value } : null;
  }

  const value = cleanText(option);
  return value ? { label: value, value } : null;
}

function getSelectedOptions(options, selectedValues) {
  const selectedSet = new Set(selectedValues);
  return options.filter((option) => selectedSet.has(option.value));
}

function CheckboxDropdownField({
  disabled = false,
  field,
  onChange,
  placeholder = 'Select options',
  value = [],
}) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const options = useMemo(
    () => (Array.isArray(field?.options) ? field.options.map(normalizeOption).filter(Boolean) : []),
    [field?.options]
  );
  const selectedValues = useMemo(
    () => (Array.isArray(value) ? value.map(cleanText).filter(Boolean) : []),
    [value]
  );
  const selectedOptions = useMemo(
    () => getSelectedOptions(options, selectedValues),
    [options, selectedValues]
  );
  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;

    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  const toggleOption = (optionValue) => {
    const selectedSet = new Set(selectedValues);

    if (selectedSet.has(optionValue)) {
      selectedSet.delete(optionValue);
    } else {
      selectedSet.add(optionValue);
    }

    onChange(options.filter((option) => selectedSet.has(option.value)).map((option) => option.value));
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(true);
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handlePanelKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div className={`checkbox-dropdown ${isOpen ? 'is-open' : ''}`} ref={rootRef}>
      <button
        aria-controls={`${field.key}-checkbox-dropdown`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`checkbox-dropdown__trigger ${selectedOptions.length ? '' : 'is-placeholder'}`}
        disabled={disabled}
        id={field.key}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
        type="button"
      >
        <span className="checkbox-dropdown__value">
          {selectedOptions.length ? (
            <>
              {selectedOptions.map((option) => (
                <span className="checkbox-dropdown__chip" key={option.value}>
                  {option.label}
                </span>
              ))}
            </>
          ) : (
            placeholder
          )}
        </span>
        <span aria-hidden="true" className="checkbox-dropdown__arrow" />
      </button>

      {isOpen ? (
        <div
          className="checkbox-dropdown__panel"
          id={`${field.key}-checkbox-dropdown`}
          onKeyDown={handlePanelKeyDown}
          role="listbox"
        >
          <input
            className="checkbox-dropdown__search"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search options"
            ref={searchRef}
            type="search"
            value={query}
          />
          <div className="checkbox-dropdown__options">
            {filteredOptions.map((option) => {
              const isChecked = selectedValues.includes(option.value);

              return (
                <label
                  className={`checkbox-dropdown__option ${isChecked ? 'is-selected' : ''}`}
                  key={option.value}
                  role="option"
                  aria-selected={isChecked}
                >
                  <input
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => toggleOption(option.value)}
                    type="checkbox"
                    value={option.value}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
            {filteredOptions.length === 0 ? (
              <span className="checkbox-dropdown__empty">
                {options.length ? 'No matching options' : 'No options configured'}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CheckboxDropdownField;
